import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MatomoTrackerService } from 'analytics';
import { OrderService, Web3ConfirmationComponent } from 'credo';
import { I18nService, TranslatePipe } from 'i18n';

import { CheckoutResponse, FunnelService } from '../../../services/funnel.service';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

/**
 * Standalone funnel payment page. Wraps the existing <app-web3-confirmation>
 * (from libs/credo) — it handles wallet connect, balance check, tx send, and
 * confirmation polling. We hand it an `orderId`, `totalPrice`, and the
 * `recipientAddress` we get back from POST /orders/:id/initiate_payment.
 *
 * On (onSuccess): order is already marked completed server-side (by ChainUtils
 * polling), so we just navigate to /quiz/success. The Order#after_commit hook
 * (Phase 5 backend) flips FunnelQuizSession to "purchased" and emails the
 * password-setup link.
 */
@Component({
  selector: 'app-funnel-payment-page',
  standalone: true,
  imports: [CommonModule, TranslatePipe, FunnelShellComponent, Web3ConfirmationComponent],
  templateUrl: './funnel-payment-page.component.html',
  styleUrls: ['./funnel-payment-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelPaymentPageComponent {
  private router       = inject(Router);
  private orderService = inject(OrderService);
  private funnel       = inject(FunnelService);
  private i18n         = inject(I18nService);
  private matomo       = inject(MatomoTrackerService);
  private destroyRef   = inject(DestroyRef);

  // Cached so we can fire trackEcommerceOrder when web3 confirms.
  private productCode  = '';
  private productName  = '';

  loading = signal(true);
  error   = signal<string | null>(null);

  orderId          = signal<number | null>(null);
  totalPrice       = signal<number>(0);
  recipientAddress = signal<string>('');

  // "Set a password / log in to finish later" — shown when payment fails.
  pwBusy  = signal(false);
  pwSent  = signal<string | null>(null);   // holds the email it was sent to
  pwError = signal(false);

  constructor() {
    const order: CheckoutResponse | undefined =
      (history.state && history.state.order) || undefined;

    if (!order) {
      // Deep-linked here without going through checkout — bounce back.
      const lang = this.i18n.getLanguage() || 'en';
      this.router.navigate(['/', lang, 'quiz', 'checkout'], { replaceUrl: true });
      return;
    }

    this.orderId.set(order.order_id);
    this.totalPrice.set(Number(order.total_with_costs ?? order.total_price));
    this.productCode = order.product?.code || 'CARD_UNKNOWN';
    this.productName = order.product?.name || this.productCode;

    this.orderService.initiatePayment(order.order_id, 'web3_payment')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.loading.set(false);
          const recipient = res?.payment_details?.recipient_address;
          if (!recipient) {
            this.error.set('Payment could not be initialised. Please try again.');
            return;
          }
          this.recipientAddress.set(recipient);
        },
        error: err => {
          this.loading.set(false);
          if (err?.status === 401) {
            // The cid auth cookie didn't make it / expired — restart funnel.
            this.funnel.clearSessionToken();
            this.backToLanding();
            return;
          }
          this.error.set(err?.error?.error || 'Could not start payment.');
        }
      });
  }

  onWeb3Success(): void {
    const oid = this.orderId();
    const total = this.totalPrice();
    if (oid && total > 0) {
      // Matomo ecommerce conversion — Matomo dedupes by orderId so this is
      // safe even if the user refreshes the success page.
      this.matomo.trackEcommerceOrder(
        { orderId: String(oid), grandTotal: total },
        [{ sku: this.productCode, name: this.productName, price: total, quantity: 1, category: 'FAC3LESS Card' }]
      );
    }
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'quiz', 'success'], { replaceUrl: true });
  }

  onWeb3Back(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'quiz', 'checkout']);
  }

  /** Email the user a password-setup / login link so they can finish later. */
  onSetPassword(): void {
    if (this.pwBusy()) return;

    this.pwBusy.set(true);
    this.pwError.set(false);

    this.funnel.requestPasswordSetup()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.pwBusy.set(false);
          this.pwSent.set(res.email || '');
        },
        error: () => {
          this.pwBusy.set(false);
          this.pwError.set(true);
        }
      });
  }

  private backToLanding(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'r'], { replaceUrl: true });
  }
}
